import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { DefinitionService } from "../../../services/DefinitionService";
import { mdlTablePagination } from "../../../models/ui-models/TablePagination";
import { PageHeaderSection } from "../../../components/common/PageHeadSection";
import { Skeleton } from "antd";
import { NotConfirmTable } from "../../../components/definitions/notConfirm/NotConfirmTable";
import { mdlHotelNotConfirmListRequest } from "../../../models/service-models/notConfirm/HotelNotConfirmListRequest";
import { HotelSelect } from "../../../components/common/HotelSelect";
import { mdlHotelNotConfirmDeleteRequest } from "../../../models/service-models/notConfirm/HotelNotConfirmDeleteRequest";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../../utils/ObjectHelper";
import _ from "lodash";
import { HeaderButton } from "../../../models/ui-models/HeaderButton";
import { faCopy, faFileExcel, faFilePdf, faPlus } from "@fortawesome/free-solid-svg-icons";
import { SFModal } from "../../../components/elements/SFModal";
import { ExportModal } from "../../../components/common/ExportModal";
import { CopyNotConfirmToOtherHotels } from "../../../components/definitions/notConfirm/CopyNotConfirmToOtherHotels";
import { enmExportModalType } from "../../../models/enums/ExportModalType";
import { NotConfirmDetail } from "../../../components/definitions/notConfirm/NotConfirmDetail";
import { mdlHotelNotConfirm } from "../../../models/domain/HotelNotConfirm";
import { mdlHotelNotConfirmIdTemplateId } from "../../../models/domain/HotelNotConfirmIdTemplateId";
import { mdlNotConfirm } from "../../../models/domain/NotConfirm";
import { enmExcelExportType } from "../../../utils/ExcelColumnMappings";

export const NotConfirmDefinition = () => {
    const { t } = useTranslation();
    const [openModalCopyToOtherHotels, setOpenModalCopyToOtherHotels] = useState<boolean>(false);
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [request, setRequest] = useState<mdlHotelNotConfirmListRequest>({ pageNumber: 1, pageSize: 10 });
    const [openModalExcelExport, setOpenModalExcelExport] = useState<boolean>(false);
    const [openModalPDFExport, setOpenModalPDFExport] = useState<boolean>(false);
    const [openModalAddNotConfirm, setOpenModalAddNotConfirm] = useState<boolean>(false);
    const [notConfirmIdTemplateIds, setNotConfirmIdTemplateIds] = useState<mdlHotelNotConfirmIdTemplateId[]>([]);

    useEffect(() => {
        if (!data && selectedHotel)
            refetch();
    }, [selectedHotel]);


    const {
        data,
        isLoading,
        refetch
    } = useQuery(
        ["getNotConfirmList", selectedHotel, request],
        () => {
            if (selectedHotel)
                return DefinitionService.NotConfirmList({ ...request, hotelId: selectedHotel });
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedHotel
        }
    );

    const handleHotelSelect = (hotelId: string) => {
        setSelectedHotel(hotelId);
    }

    const handleTableParamsChange = (pagination: mdlTablePagination, filters: any) => {
        setRequest({ ...request, pageSize: pagination.pageSize ?? 10, pageNumber: pagination.current ?? 1 })
    }

    const handleDelete = (record: mdlNotConfirm) => {
        deleteNotConfirm({
            notConfirmIdTemplateId: {
                notConfirmMessageId: record.id,
                notConfirmMessageTemplateId: record.notConfirmMessageTemplateId
            }
        });
    }

    const { mutateAsync: deleteNotConfirm, isLoading: mutateLoading, data: mutateData } = useMutation(
        (request: mdlHotelNotConfirmDeleteRequest) => DefinitionService.NotConfirmDelete(request),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    toast.success(t("common.success"));
                    refetch();
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const handleAddNew = () => {
        setOpenModalAddNotConfirm(true);
    }

    const exportToExcel = () => {
        setOpenModalExcelExport(true);
    }

    const exportToPDF = () => {
        setOpenModalPDFExport(true);
    }

    const copyToOtherHotels = () => {
        if (notConfirmIdTemplateIds.length > 0) {
            setOpenModalCopyToOtherHotels(true);
        }
        else {
            toast.error(t("common.pleaseSelectAtLeastOneRecord"));
        }
    }

    const getButtons = (): HeaderButton[] | undefined => {
        let _buttons: HeaderButton[] = [];
        _buttons.push({
            title: t("definitions.notConfirm.addNew"),
            icon: faPlus,
            fnFunction: handleAddNew,
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        _buttons.push({
            title: t("definitions.hotels.copyToOtherHotels"),
            icon: faCopy,
            fnFunction: copyToOtherHotels,
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        _buttons.push({
            title: t("common.exportToExcel"),
            icon: faFileExcel,
            fnFunction: exportToExcel,
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        _buttons.push({
            title: t("common.pdf"),
            icon: faFilePdf,
            fnFunction: exportToPDF,
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        return _buttons;
    }

    const handleDetailCallback = () => {
        setOpenModalAddNotConfirm(false);
        refetch();
    }

    const handleCopyNotConfirmsToOtherHotelsCallback = () => {
        setOpenModalCopyToOtherHotels(false);
        refetch();
    }

    return (
        <>
            <SFModal
                width={window.innerWidth * 30 / 100}
                open={openModalExcelExport}
                toggler={() => { setOpenModalExcelExport(!openModalExcelExport) }}
                body={<ExportModal excelExportType={enmExcelExportType.NotConfirmed} name="notComfirmReasons" data={data?.body?.data} fnModalClose={() => setOpenModalExcelExport(!openModalExcelExport)} />}
                title={t("common.exportToExcel")}
            />
            <SFModal
                width={window.innerWidth * 30 / 100}
                open={openModalPDFExport}
                toggler={() => { setOpenModalPDFExport(!openModalPDFExport) }}
                body={<ExportModal excelExportType={enmExcelExportType.NotConfirmed} name="notComfirmReasons" type={enmExportModalType.PDFTable} data={data?.body?.data} fnModalClose={() => setOpenModalPDFExport(!openModalPDFExport)} />}
                title={t("common.exportToPDF")}
            />
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openModalAddNotConfirm}
                toggler={() => { setOpenModalAddNotConfirm(!openModalAddNotConfirm) }}
                body={<NotConfirmDetail hotelId={selectedHotel} callback={handleDetailCallback} />}
                title={t("definitions.notConfirm.addNew")}
            />
            <SFModal
                open={openModalCopyToOtherHotels}
                toggler={() => { setOpenModalCopyToOtherHotels(!openModalCopyToOtherHotels) }}
                body={<CopyNotConfirmToOtherHotels notConfirmIdTemplateIds={notConfirmIdTemplateIds} fromHotelId={selectedHotel} callback={handleCopyNotConfirmsToOtherHotelsCallback} />}
                title={t("definitions.hotels.copyToOtherHotels")}
            />
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection
                                pageTitle={t("definitions.notConfirm.title")}
                                buttons={getButtons()}
                            />
                            <div className="nk-block">
                                <div className="row g-gs">
                                    <div className="col-12">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                {
                                                    <div className="mb-4">
                                                        <HotelSelect onChange={handleHotelSelect} />
                                                    </div>
                                                }
                                                {
                                                    isLoading
                                                    &&
                                                    <>
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                    </>
                                                }
                                                {
                                                    data
                                                    &&
                                                    <NotConfirmTable
                                                        onDelete={handleDelete}
                                                        pagination={{
                                                            align: "center",
                                                            current: data?.body?.pageNumber,
                                                            pageSizeOptions: [10, 20, 50],
                                                            defaultPageSize: 10,
                                                            pageSize: request.pageSize,
                                                            defaultCurrent: 1,
                                                            responsive: true,
                                                            showSizeChanger: true,
                                                            total: data?.body?.resultCount,
                                                            showTotal: (total: number, range: [number, number]) => t("pagination.total") + ` ${data?.body?.resultCount} ` + t("pagination.result"),
                                                            locale: { items_per_page: "/ " + t("pagination.itemPerPage") }
                                                        }}
                                                        onTableParamsChange={handleTableParamsChange}
                                                        tableItems={data?.body?.data}
                                                        rowSelectionChange={(hotelNotConfirms: mdlHotelNotConfirm[]) => setNotConfirmIdTemplateIds(hotelNotConfirms?.map(x => ({ notConfirmMessageId: x.id, notConfirmMessageTemplateId: x.notConfirmMessageTemplateId })))}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};